// src/app/hooks/useCognitionSetup.ts
import { useEffect, useRef, useState } from "react";
import { fetchCognitionCandidates } from "@/app/services/cognition/cognition-api";
import type { CognitionCandidatesResponse, AutoExtractedProduct, BrandSnapshot } from "@/app/services/cognition/cognition-service";

export function useCognitionSetup(brandName: string) {
  const [products, setProducts] = useState<AutoExtractedProduct[]>([]);
  const [brandSnapshot, setBrandSnapshot] = useState<BrandSnapshot | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const lastSuccessfulBrandRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    const b = (brandName || "").trim();

    if (!b) {
      lastSuccessfulBrandRef.current = null;
      inFlightRef.current = false;
      setProducts([]);
      setBrandSnapshot(null);
      setError(null);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    if (lastSuccessfulBrandRef.current === b && !inFlightRef.current) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    if (inFlightRef.current) {
      return () => {
        mounted = false;
      };
    }

    inFlightRef.current = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res: CognitionCandidatesResponse = await fetchCognitionCandidates(b);
        if (!mounted) return;

        setProducts(res.autoExtractedProducts ?? []);
        setBrandSnapshot(res.brandSnapshot ?? null);

        lastSuccessfulBrandRef.current = b;
      } catch (e: any) {
        if (!mounted) return;
        setError(e instanceof Error ? e : new Error("Failed to load cognition candidates"));
      } finally {
        if (!mounted) return;
        inFlightRef.current = false;
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [brandName]);

  return { products, brandSnapshot, loading, error };
}
